import { defineStore } from 'pinia'

let reloadListFunc = null

// initial state
const state = () => ({
  selectableMap: new Map(), // map with all selectable elements
  // selectedList: [], // current selected items from list
  key: 'id', // how to index if item is selected in object
  parentKey: null, // how to index if item is selected in object
  selected: new Map(), // object -> key => [ parentId, num. of children selected, num. of children total, self selected ]
  addedToSelectableListKeys: null,
  defaultSelected: null,
  previousItemSelectedIndex: null,
})

// functionName: (state, getters, rootState) => {}
const getters = {
  childrenMap() {
    const hashTable = new Map()
    this.selectableMap.forEach((element) => {
      const parentId = element[this.parentKey] || 0

      if (!hashTable.has(parentId)) {
        hashTable.set(parentId, [])
      }

      hashTable.get(parentId).push(element)
    })
    return hashTable
  },
  listChildrenRecursive() {
    return (parentId, children = []) => {
      this.childrenMap.get(parentId)?.forEach(child => {
        children.push(child)
        this.listChildrenRecursive(child.id, children)
      })
      return children
    }
  },
  listParents() {
    return (childId) => {
      if (!childId || !this.selectableMap.has(childId))
        return []
      
      const parents = []
      let parentId = this.selectableMap.get(childId)[this.parentKey]
      while (parentId) {
        parents.push(this.selectableMap.get(parentId))
        parentId = this.selectableMap.get(parentId)[this.parentKey]
      }
      return parents
    }
  },
  listSelected() {
    const selected = []
    this.selected.forEach((val, id) => {
      selected.push(this.selectableMap.get(id))
    })
    return selected
  },
  list() {
    if (this.listSelected.length) {
      return this.listSelected;
    }

    if (this.defaultSelected === false) {
      return []
    }

    if (this.defaultSelected === null) {
      return [...this.selectableMap.values()]
    }

    return [ this.defaultSelected ]
  },
  listSelectedRoots() {
    if (this.selected.size === 0 && this.defaultSelected !== false) {
      if (this.defaultSelected === null) {
        return [...this.selectableMap.values()]
      } else {
        return [ this.defaultSelected ]
      }
    }

    const arr = []
    this.selected.forEach((val, id) => {
      if (val[0] === null)
        arr.push(this.selectableMap.get(id))
    })

    return arr
  },
  listSelectedChildren() {
    return (parentId) => {
      const children = []
      
      this.selected.forEach((val, id) => {
        if (val[0] === parentId)
          children.push(this.selectableMap.get(id))
      })
      return children
    }
  },
  listSelectedChildrenRecursive() {
    return (parentId) => {
      const children = []
      
      this.listSelectedChildren(parentId).forEach(child => {
        children.push(child)
        const recursiveChildren = this.listSelectedChildrenRecursive(child.id)
        if (recursiveChildren.length) {
          children.push(...recursiveChildren)
        }
      })
      return children
    }
  },
  // list selected items with no children OR all children selected
  listFullySelected() {
    const ret = []
      
    this.selected.forEach((val, id) => {
      if (val[3] && this.selectableMap.has(id))
        ret.push(this.selectableMap.get(id))
    })

    return ret
  },
};

// functionName ({ commit, state, dispatch }, param, ...) {}
const actions = {
  select(item, options = { isRecursive: true }) {
    document.getSelection().empty()

    const id = item[this.key]
    if (!id) { // id can be null or undefined, so skip select then
      return
    }

    // is item selected?
    if (this.selected.has(id) && this.selected.get(id)[3]) {
      // unselect

      // do have selected children?
      if (this.selected.get(id)[1] > 0) {
        this.childrenMap.get(id)?.forEach(element => {
          if (this.selected.has(element[this.key])) {
            this.select(
              element, 
              {
                ...options,
                ...{
                  isRecursive: true
                }
              }
            )
          }
        })
      } else { // do have parent?
        this.unselect(id)
      }
    } else { // select
      // multiple shift select
      if (
          options.isShift && 
          (options.itemIndex >= 0 || options.isDocument)
        ) {
        const direction = options.itemIndex > this.previousItemSelectedIndex ? 1 : -1
        const startIndex = this.previousItemSelectedIndex + direction
        const endIndex = options.itemIndex

        for (
          let currentIndex = startIndex;
          direction > 0 ? currentIndex <= endIndex : currentIndex >= endIndex;
          currentIndex += direction
        ) {
          let element, subElements

          if (options.enableSubItemSelect) {
            subElements = document.querySelectorAll('[data-item-index="' + currentIndex + '"]')
          } else {
            element = document.querySelector('[data-item-index="' + currentIndex + '"]')
          }

          if (!subElements) {
            const itemKey = element.item[this.key]

            if (
              !(options.isDocument && element.item.children?.length > 0 && currentIndex !== endIndex) && 
              !this.selected.has(itemKey)
            ) {
              this.select(
                element.item, 
                {
                  ...options, 
                  ...{
                    isShift: false,
                    itemIndex: currentIndex,
                  }
                }
              )
            }
          } else {
            subElements.forEach(subElement => {
              const subItemKey = subElement.item[this.key]

              if (!this.selected.has(subItemKey)) {
                this.select(
                  subElement.item, 
                  {
                    ...options, 
                    ...{
                      isShift: false,
                      itemIndex: currentIndex,
                      enableSubItemSelect: false,
                    }
                  }
                )
              }
            })
          }
        }

        return
      }

      this.previousItemSelectedIndex = options.itemIndex

      if (this.addedToSelectableListKeys !== null && !this.addedToSelectableListKeys.has(id)) {
        this.addedToSelectableListKeys.add(id)
        this.selectableMap.set(id, item)
      }

      // select parents
      if (this.parentKey && item[this.parentKey]) {
        if (!this.selected.has(item[this.parentKey])) { // parent is already selected
          this.select(
            this.selectableMap.get(item[this.parentKey]), 
            {
              ...options,
              ...{
                isRecursive: false
              }
            }
          )
        }
        
        if (!this.selected.has(id)) {
          this.selected.get(item[this.parentKey])[1]++
        }
      }

      // select self
      if (!this.selected.has(id)) {
        this.selected.set(id, [
          this.parentKey && item[this.parentKey] ? item[this.parentKey] : null,
          0,
          this.parentKey && this.childrenMap.has(id) ? this.childrenMap.get(id).length : 0,
          options.isRecursive || !this.childrenMap.has(id)
        ])
      } else {
        this.selected.get(id)[3] = options.isRecursive || !this.childrenMap.has(id)
      }

      // select children
      if (options.isRecursive && this.parentKey) {
        this.childrenMap.get(id)?.forEach(element => {
          if (!this.selected.has(element[this.key])) {
            this.select(
              element, 
              {
                ...options,
                ...{
                  isRecursive: true
                }
              }
            )
          }
        })
      }
    }
  },
  reset (list, key, parentKey, defaultSelected) {
    this.key = key
    this.parentKey = parentKey
    reloadListFunc = null
    this.previousItemSelectedIndex = null
    
    if (list === null) {
      this.selectableMap = new Map()
      this.addedToSelectableListKeys = new Set()
    
    } else if (typeof list === 'function') {
      reloadListFunc = list
      this.selectableMap = reloadListFunc()
      this.addedToSelectableListKeys = null
    
    } else {
      this.selectableMap = list
      this.addedToSelectableListKeys = null
    }
    this.selected.clear()
    this.defaultSelected = defaultSelected !== undefined ? defaultSelected : null
  },
  unselect (itemId) {
    if (this.selected.get(itemId)[0] !== null && this.selected.has(this.selected.get(itemId)[0])) {
      this.subSelectedChild(this.selected.get(itemId)[0])
    }
    
    if (this.addedToSelectableListKeys !== null) {
      if (this.addedToSelectableListKeys.has(itemId)) {
        this.addedToSelectableListKeys.delete(itemId)
        this.selectableMap.delete(itemId)
      }
    }
    
    this.selected.delete(itemId)
  },
  subSelectedChild(parentId) {
    const selected = this.selected.get(parentId)
    selected[1]--
    selected[3] = false
    if (selected[1] === 0) {
      this.unselect(parentId)
    }
  },
  resetSelected () {
    this.selected.clear()
    this.previousItemSelectedIndex = null

    if (this.addedToSelectableListKeys !== null) {
      this.addedToSelectableListKeys.clear()
      this.selectableMap.clear()
    }
  },
  reload () {
    this.resetSelected()
    if (reloadListFunc) {
      this.selectableMap = reloadListFunc()
      // this.selectableMap.push(savedElement)
    }
  },
  selectAll() {
    this.childrenMap.get(0)?.forEach(item => {
      this.select(item)
    })
  },
  isFullySelected(item) {
    return this.selected.has(item[this.key])
      && this.selected.get(item[this.key])[3]
      && this.selectableMap.has(item[this.key])
  }
}

export default defineStore({
  id: 'bcSelected',
  state,
  getters,
  actions,
});
